import { contextUnhandledMessage } from '../../common/context/constants.ts';
import type { Domain, HandleReturnType, StateToMessagesReturnType } from '../../domain.ts';
import { DomainKeys, type Peer, type State } from '../../state/types/state.ts';
import type { DomainInfo } from '../../state/types/domain-registry.ts';
import { broadcast, error, isLocalSource, isRemoteSource, success, unicast } from '../../common/messages.ts';
import { join as gJoin } from '../global/messages.ts'
import { exceptionToReason, reason, requestToReason } from '../../reason.ts';
import { getLogger } from '../../logger.ts';
import {
    announcePeer,
    byId,
    byIdInDomain,
    byIdInDomainThrows,
    bySourceInDomain,
    isClusterPeer,
    type JoinedPeer,
    peersInDomain,
    updatePeer
} from '../../state/peers.ts';
import { inDomain, joinDomain, leaveDomain } from '../../state/core.ts';
import * as ctx from '../../state/types/context.ts'
import type { Context } from '../../state/types/context.ts'
import {
    announceContexts,
    compatibilityModeFilter,
    type ContextOverrides,
    forwardCreate,
    forwardDestroy,
    forwardSubscribe,
    forwardUnsubscribe,
    forwardUpdate,
    peerToDomainUri,
    removePeer
} from '../../common/context/ops.ts';
import { contextDomainUri, failure, reasonPeerRemoved } from './constants.ts';
import { peerToAddress } from '../../address.ts';
import { nodeId } from '../../common/ids.ts';
import { globalDomainUri } from '../global/constants.ts';
import type {
    CommandType,
    ContextRequestType,
    DomainType,
    MessageType,
    NodeMessage,
    SendMessageAction
} from '../../node.ts';
import { parse } from '../../restrictions.ts';
import type { Identity, NodeAddress, OriginType, PeerId, Restrictions, Source } from '../../state/types/common.ts';
import type { DomainJoin, Leave, RemoteJoin } from '../global/types/messages.ts';
import { contexts, isClusterContext } from '../../common/context/state.ts';
import { createContext, subscribe } from './messages.ts';
import type { Draft } from 'immer';
import { IOGateway } from '../../../gateway';

const log = getLogger('gateway.domains.context');

// conversion functions

function contextToRequest(peerId: PeerId, ctx: Context) {
    return createContext(
        undefined as unknown as string, // requestId
        peerId,
        ctx.name,
        ctx.data,
        ctx.lifetime,
        ctx.read_permissions,
        ctx.write_permissions,
        ctx.version);
}

function announceJoinedPeer(state: State, receiver: Source, peerId: PeerId, wasInCompat?: boolean): SendMessageAction<'context-added' | 'peer-added'>[] {
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Context);
    let announceMessages: SendMessageAction<'context-added' | 'peer-added'>[] =
        announcePeer(contextDomainUri, DomainKeys.Context, state, receiver, peer)
            .filter((m) => compatibilityModeFilter(state, m));
    if (wasInCompat) {
        // if the peer was already implicitly joined, then its already announced
        announceMessages = announceMessages.filter((m) => peerId !== m.body['new_peer_id']);
    }
    // if the peer was implicitly joined already, it has received the contexts
    const contextMessages: SendMessageAction<'context-added' | 'peer-added'>[] = !wasInCompat ? announceContexts(peerToDomainUri(peer), state, peer) : [];
    return announceMessages.concat(contextMessages);
}

function join(state: State, source: Source, peerId: PeerId, peerIdentity: Identity, restrictions: Restrictions, wasInCompat?: boolean): HandleReturnType {
    let nextState = joinDomain(state, peerId, ctx.contextDomain, restrictions);
    //todo log action
    if (wasInCompat) {
        nextState = updatePeer(nextState, peerId, (draft: Draft<Peer>) => {
            if (draft.options) {
                delete draft.options["context-compatibility-mode?"];
                if (Object.keys(draft.options).length === 0) {
                    delete draft.options;
                }
            }
        });
    }
    return [nextState, announceJoinedPeer(nextState, source, peerId, wasInCompat)];
}

function localJoin(state: State, source: Source, request: DomainJoin): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, identity: peerIdentity, restrictions} = request;
    const contextCompatMode = request.options?.["context-compatibility-mode?"]
    const wasInCompat = byId(state, peerId)?.options?.["context-compatibility-mode?"];
    const alreadyHere = inDomain(state, peerId, ctx.contextDomain);

    if (alreadyHere && !wasInCompat) {
        // already in the domain
        return [state, [success(contextDomainUri, source, requestId, peerId)]]
    } else {
        // we need to join it
        const parsedRestrictions = parse(restrictions);
        const [nextState, msgs] = join(state, source, peerId, peerIdentity, parsedRestrictions, alreadyHere && wasInCompat);
        const peer = byId(nextState, peerId);

        const messages: SendMessageAction<MessageType>[] = []
        if (!contextCompatMode) {
            messages.push(success(contextDomainUri, source, requestId, peerId));
        }
        if (isClusterPeer(DomainKeys.Context, peer)) {
            const message: Omit<RemoteJoin, 'restrictions'> & { restrictions?: unknown } = {
                ...request,
                domain: globalDomainUri,
                type: 'join',
                destination: contextDomainUri,
                identity: peerIdentity
            };
            if (peer?.options) {
                message.options = peer.options;
            }
            if (parsedRestrictions) {
                message.restrictions = parsedRestrictions;
            }
            messages.push(broadcast(peerToAddress(nodeId(state.ids), peerId), message));
        }
        return [nextState, msgs.concat(messages)];
    }
}

function remoteJoin(state: State, source: Source, request: RemoteJoin): HandleReturnType {
    const {peer_id: peerId, identity: peerIdentity, restrictions} = request;
    const wasInCompat = byId(state, peerId)?.options?.["context-compatibility-mode?"];
    if (!(wasInCompat && inDomain(state, peerId, ctx.contextDomain))) {
        return join(state, source, peerId, peerIdentity, restrictions, wasInCompat);
    }
    return [state, []];
}

export function forwardJoin(state: State, source: Source, request: Omit<DomainJoin, 'type'>): HandleReturnType {
    return isRemoteSource(source) ? remoteJoin(state, source, request as RemoteJoin) : localJoin(state, source, request as DomainJoin);
}

function remoteLeave(state: State, source: Source, request): HandleReturnType {
    const {peer_id: peerId} = request;
    const peer = byIdInDomain(state, peerId, DomainKeys.Context);
    if (peer) {
        return removePeer(contextDomainUri, state, peer, requestToReason(request), false);
    }
    return [state, []];
}

function localLeave(state: State, source: Source, request): HandleReturnType {
    const {request_id: requestId, peer_id: peerId} = request;
    const [newState, msg] = removePeer(contextDomainUri, state, byIdInDomainThrows(state, peerId, DomainKeys.Context), requestToReason(request), false);

    return [
        newState,
        msg.concat([
            success(contextDomainUri, source, requestId, peerId),
            broadcast(
                peerToAddress(nodeId(newState.ids), peerId),
                {
                    ...request,
                    type: 'leave'
                })])];

}

export function forwardLeave(state: State, source: Source, request): HandleReturnType {
    return isRemoteSource(source) ? remoteLeave(state, source, request) : localLeave(state, source, request);
}

export function sendState(state: State, receiver: NodeAddress): StateToMessagesReturnType {
    const nodeId = state.ids.nodeId;
    const localPeerIds = new Set<PeerId>();
    const joinMessages: StateToMessagesReturnType = peersInDomain(state, DomainKeys.Context)
        .filter(peer => isLocalSource(peer.source) && isClusterPeer(DomainKeys.Context, peer))
        .map(peer => {
            const peerId = peer.id;
            localPeerIds.add(peerId);
            return unicast(
                peerToAddress(nodeId, peerId),
                receiver,
                gJoin(undefined, peerId, peer[DomainKeys.Context].restrictions, contextDomainUri, peer.identity, peer.options));
        });
    const createMessages: StateToMessagesReturnType = contexts(state)
        .filter(ctx => (ctx.local === true) && isClusterContext(ctx))
        .flatMap(ctx => {
            const localMembers = Array.from(ctx.members).filter(peerId => localPeerIds.has(peerId));
            // it is not obvious which peer should create the context in the remote nodes in case the peer is not present anymore
            const peerId: PeerId = ctx.owner ?? localMembers[0];
            const creates: StateToMessagesReturnType = [unicast(peerToAddress(nodeId, peerId), receiver, contextToRequest(peerId, ctx))];
            const subscribes: StateToMessagesReturnType = Array.from(localMembers)
                .map((member) => {
                    return unicast(peerToAddress(nodeId, member), receiver, subscribe(undefined as unknown as string, member, ctx));
                });
            return creates.concat(subscribes);
        });
    const messages = joinMessages.concat(createMessages);
    return messages;
}

/**
 * Source has disconnected from the system
 */
export function sourceRemoved(state: State, source: Source, _request: IOGateway.Message<'commands/source-removed'>): HandleReturnType {
    if (log.enabledFor('debug')) {
        log.debug(`removing source ${JSON.stringify(source)} from context domain`);
    }
    const affected = bySourceInDomain(state, source, ctx.contextDomain);
    const stateWithoutAffected = affected
        .map(p => p.id)
        .reduce((s, peerId) => {
            return leaveDomain(s, peerId, ctx.contextDomain);
        }, state);
    const leaveRequest: Omit<Leave, 'peer_id'> = {
        domain: globalDomainUri,
        type: 'leave',
        destination: contextDomainUri,
        reason_uri: reasonPeerRemoved.uri,
        reason: reasonPeerRemoved.message
    };
    const r: HandleReturnType = affected.reduce(([s, ms]: HandleReturnType, peer: JoinedPeer<DomainKeys.Context>) => {
        const [ns, nms]: HandleReturnType = removePeer(contextDomainUri, s, peer, reasonPeerRemoved, true);

        const bs: SendMessageAction<MessageType>[] = [];
        if (isLocalSource(peer.source) && isClusterPeer(DomainKeys.Context, peer)) {
            const message: Leave = {
                ...leaveRequest,
                peer_id: peer.id
            };
            bs.push(broadcast(peerToAddress(nodeId(state.ids), peer.id), message));
        }
        return [ns, ms.concat(nms.concat(bs))] as HandleReturnType;
    }, [stateWithoutAffected, []] as HandleReturnType);
    if (log.enabledFor('debug')) {
        log.debug(`removed source ${JSON.stringify(source)} from context domain`);
    }
    return r;
}

export function handleRequest(state: State, source: Source, request, options?: ContextDomainOptions): HandleReturnType {

    switch (request.type) {
        case 'domain/join': {
            return forwardJoin(state, source, request as DomainJoin);
        }
        case 'domain/leave': {
            return forwardLeave(state, source, request);
        }
        case 'create-context': {
            return forwardCreate(contextDomainUri, state, source, request, options);
        }
        case 'update-context': {
            return forwardUpdate(contextDomainUri, state, source, request);
        }
        case 'subscribe-context': {
            return forwardSubscribe(contextDomainUri, state, source, request);
        }
        case 'unsubscribe-context': {
            return forwardUnsubscribe(contextDomainUri, state, source, request);
        }
        case 'destroy-context': {
            return forwardDestroy(contextDomainUri, state, source, request);
        }
        case 'commands/source-removed': {
            return sourceRemoved(state, source, request);
        }

        default: {
            log.error(`Unhandled message ${JSON.stringify(request)}`);
            return [state, [error(contextDomainUri, source, request['request_id'] ?? -1, request['peer_id'], reason(contextUnhandledMessage(contextDomainUri), `Unhandled message ${JSON.stringify(request)}`))]];
        }
    }
}


class ContextDomain implements Domain<ContextRequestType> {

    constructor(private readonly options?: ContextDomainOptions) {}

    destroy(state: State): State {
        return state;
    }

    handleMessage(state: State, request: NodeMessage<CommandType | DomainType | ContextRequestType>): HandleReturnType {
        const {source, body} = request;
        try {
            return handleRequest(state, source, body, this.options);
        } catch (e) {
            if (isLocalSource(source)) {
                return [state, [error(contextDomainUri, source, body['request_id'], body['peer_id'], exceptionToReason(e, failure))]]
            } else {
                return [state, []]
            }
        }
    }

    info(): DomainInfo {
        return {uri: contextDomainUri, description: '', version: 3};
    }

    init(state: State): State {
        return state;
    }

    stateToMessages(state: State, receiver: NodeAddress): StateToMessagesReturnType {
        return sendState(state, receiver);
    }
}
export type ContextDomainOptions = ContextOverrides & {
    defaultPeerRestrictions?: OriginType;
}

export function contextDomain(options?: ContextDomainOptions) {
    return new ContextDomain(options);
}
